import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useSearchParams } from 'react-router-dom';
import { GhlService } from '../../../services/ghl/ghl.service';

const fontStack = '"Inter", system-ui, -apple-system, sans-serif';

const FEATURES: { id: string; title: string; description: string; launchDescription?: string }[] = [
  {
    id: 'webinars',
    title: 'Webinars',
    description: 'Live, Simulive & Evergreen sessions',
    launchDescription: 'Ultra-HD live webinars. Zero lag.',
  },
  { id: 'ai_builder', title: 'AI Webinar Builder', description: 'Launch complete funnels in minutes, no setup' },
  { id: 'easycrm', title: 'EasyCRM', description: 'Lead capture, scoring & automated follow-up' },
  { id: 'multistream', title: 'Multistream', description: 'Broadcast live to YouTube, Facebook & LinkedIn' },
  { id: 'paid_webinars', title: 'Paid Webinars', description: 'Sell directly from every webinar session' },
  { id: 'checkout', title: 'Built-in Checkout', description: 'Sell directly from every webinar session' },
  { id: 'registration_pages', title: 'Registration Pages', description: 'Automated reminders, follow-ups & replays' },
  { id: 'email_sequences', title: 'Email Sequences', description: 'Automated reminders, follow-ups & replays' },
  { id: 'analytics', title: 'Analytics & Insights', description: 'Attendee behaviour, drop-off & conversion data' },
  { id: 'integrations', title: 'Integrations', description: 'Connect your CRM, email & payment tools' },
];

interface Step5Props {
  onComplete: () => void;
  onBack: () => void;
  ghlContactId: string;
}

export default function Step5({ onComplete, onBack, ghlContactId }: Step5Props) {
  const [searchParams] = useSearchParams();
  const planId = (searchParams.get('planId') || localStorage.getItem('planId') || '').toLowerCase();
  const isLaunchPlan = planId?.toLowerCase().includes('live-webinars');
  const visibleFeatures = isLaunchPlan
    ? FEATURES.filter((feature) => feature.id !== 'easycrm' && feature.id !== 'paid_webinars')
    : FEATURES;

  return (
    <Box sx={{ fontFamily: fontStack }}>
      <Typography
        component="h1"
        sx={{
          textAlign: 'center',
          color: '#111827',
          fontSize: { xs: '1.35rem', sm: '1.6rem' },
          fontWeight: 700,
          letterSpacing: '-0.02em',
          lineHeight: 1.25,
          mb: 2,
        }}
      >
        Your 7-day trial unlocks everything
      </Typography>

      <Box
        sx={{
          borderRadius: '12px',
          bgcolor: '#FFF7E6',
          border: '1px solid #F5E6C8',
          px: 2,
          py: 1.5,
          mb: 3,
          display: 'flex',
          justifyContent: 'center',
          gap: 1.25,
          alignItems: 'center',
        }}
      >
        <Typography component="span" sx={{ fontSize: '1.1rem', lineHeight: 1.2, flexShrink: 0, textAlign: 'center' }}>
        🎁
        </Typography>
        <Typography sx={{ color: '#78350F', fontSize: '0.85rem', lineHeight: 1.45, fontWeight: 500, textAlign: 'center' }}>
          Everything below is fully active from day one —<br /> no charge until after your 7-day trial ends.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          rowGap: 1.5,
          columnGap: 3,
          mb: 3,
        }}
      >
        {visibleFeatures.map((f) => (
          <Box
            key={f.title}
            sx={{
              display: 'flex',
              gap: 1.25,
              py: 1.5,
              px: 2,
              borderRadius: '10px',
              border: '1px solid #ECECF1',
              bgcolor: '#F9FAFB',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: '#111827',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.15,
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 14, color: '#FFFFFF' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                {f.title}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', mt: 0.15, lineHeight: 1.35 }}>
                {isLaunchPlan && f.launchDescription ? f.launchDescription : f.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={() => {
          onComplete();
          GhlService.addTags(ghlContactId, ["USP page"]);
        }}
        endIcon={<span aria-hidden>→</span>}
        sx={{
          textTransform: 'none',
          borderRadius: '10px',
          py: 1,
          fontSize: '0.92rem',
          fontWeight: 600,
          color: '#FFFFFF',
          bgcolor: '#4058fb',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#3442bd', boxShadow: 'none' },
          '&.Mui-disabled': {
            bgcolor: '#4058fb',
            color: '#C5C7D0',
            opacity: 0.6,
            cursor: 'not-allowed',
            boxShadow: 'none'
          },
        }}
      >
        Activate my free trial
      </Button>

      <Button
        fullWidth
        variant="text"
        onClick={onBack}
        startIcon={<span aria-hidden>←</span>}
        sx={{
          mt: 1.25,
          textTransform: 'none',
          color: '#3B82F6',
          fontSize: '0.9rem',
          fontWeight: 500,
        }}
      >
        Back
      </Button>
    </Box>
  );
}
